/* eslint-disable */
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { CorePanhandlePlansName, CorePlansName, VEB_PLAN_ARRAY, VEB_PLAN_NAMES } from "../../../../constants/plan";
import { dateTimeZoneConverter } from "../../../../utils/commonFunctions/date";
import { Button, IconButton, Menu, MenuItem, Paper, Popover, Typography } from "@mui/material";
import { FaInfoCircle } from "react-icons/fa";
import { HiOutlinePencilSquare } from "react-icons/hi2";

import { enrollmentInfoGridProps } from "../../../../types/enrollmentV2.types";
import "./enrollmentInfo.css";
import { GROUP_NUMBER } from "../../../../constants/groups";
import { AuthContext } from "../../../../contexts";
import { formatTwoDecimalPlaces } from "../../../../utils/commonFunctions/decimal";
import { useVEBEnrollmentData } from "../../../../hooks/useVEBEnrollment";
import { useGetActiveVEBPlansQuery } from "../../../../features/VEBPlans/vebPlansApi";
const EnrollmentInfoGrid: React.FC<enrollmentInfoGridProps> = ({
	plan_code,
	plan_name,
	sub_plan,
	base_plan_name,
	enrollment_date,
	initial_effective_date,
	effective_date,
	benefit_end_date,
	termination_date,
	coreDependentsCount,
	vebDependentsCount,
	coreTrueCount,
	vebTrueCount,
	vebRiderCount,
	handleClick,
	handleVebRiderMenuClick,
	enrollmentsLength,
	enrollments,
	handleVebBeneficiaryMenuClick,
	handleOpenCloseEditBeneficiaryModal,
	beneficiaries_required,
	currentPlans,
	futurePlans,
	corePlans
}) => {
	const { member } = useContext(AuthContext);

	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [isOpen, setIsOpen] = useState(false);
	const [enrollmentForCurrentMember, setEnrollmentForCurrentMember] = useState<any>(enrollments);

	// Derived booleans
	const isPanhandle = useMemo(
		() => enrollments.length > 0 && enrollments[0].group_number === GROUP_NUMBER.BOP_PANHANDLE_FIRST_BANK,
		[enrollments]
	);

	// const isCorePlan = useMemo(() => CorePlansName.includes(plan_name), [plan_name]);
	const isCorePlan = useMemo(() => {
		if (!corePlans?.length) return false;

		return corePlans.some((plan) => plan.plan_name === plan_name);
	}, [corePlans, plan_name]);

	//  Now useing the  currentPlans from props
	const isVebPlan = useMemo(() => {
		if (currentPlans.length > 0) {
			return currentPlans.some((plan) => plan.plan_name === plan_name);
		}
		return !isCorePlan && VEB_PLAN_ARRAY.includes(plan_name);
	}, [currentPlans, plan_name, isCorePlan]);

	const isPanhandlePlan = useMemo(
		() => plan_name === CorePanhandlePlansName[0] && isPanhandle,
		[plan_name, isPanhandle]
	);

	// Filtering the beneficiary count
	const filteredBenificaryLength = useMemo(
		() =>
			enrollments.filter(
				(enrollment) =>
					enrollment.VEB_primary_beneficiary?.length >= 1 ||
					enrollment.primary_beneficiery?.length >= 1 ||
					enrollment.VEB_contingent_beneficiary?.length >= 1 ||
					enrollment.contingent_beneficiery?.length >= 1
			).length,
		[enrollments]
	);

	// Benefit amount
	const benefit_amount = useMemo(() => {
		try {
			return enrollmentForCurrentMember[0]?.benefit_amount || 0;
		} catch {
			return 0;
		}
	}, [enrollmentForCurrentMember]);

	const showBenefitAmount = benefit_amount > 0;

	// Handlers
	const handleBenefitViewClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
		setIsOpen((prev) => !prev);
		setAnchorEl(event.currentTarget);
	}, []);

	const handleClose = useCallback(() => {
		setAnchorEl(null);
		setIsOpen(false);
	}, []);

	// Filter enrollment for current member
	useEffect(() => {
		if (!enrollments?.length) return;
		const current = enrollments
			.filter(
				(enrollment: any) =>
					enrollment.member_SSN === member.SSN && enrollment.plan_name === enrollment.base_plan_name
			)
			.sort((a: any, b: any) =>
				a.enrollment_date < b.enrollment_date ? 1 : a.enrollment_date > b.enrollment_date ? -1 : 0
			);

		if (current.length > 0) {
			setEnrollmentForCurrentMember([current[0]]);
		}
	}, [enrollments, member.SSN]);

	//  Styles
	const columnStyle: React.CSSProperties = useMemo(
		() => ({
			fontSize: "13px",
			fontWeight: 600,
			color: "#333",
			padding: "8px",
			minWidth: "100px",
			textAlign: "left",
			overflowWrap: "break-word"
		}),
		[]
	);

	const cellStyle: React.CSSProperties = useMemo(
		() => ({
			fontSize: "14px",
			color: "#212121",
			padding: "10px 8px",
			overflowWrap: "break-word"
		}),
		[]
	);

	// Stacked enrollment helpers
	const baseEnrollment = enrollmentForCurrentMember?.[0];
	const stackedEnrollments = baseEnrollment?.stacked_enrollments;
	const hasStacked = stackedEnrollments?.length > 0;

	const displayBenefitAmount = hasStacked ? stackedEnrollments[0].benefit_amount : baseEnrollment?.benefit_amount;
	const displayEnrollmentDate = hasStacked ? stackedEnrollments[0].enrollment_date : baseEnrollment?.enrollment_date;
	const displayTotalPremium = hasStacked ? stackedEnrollments[0].total_premium : baseEnrollment?.total_premium;

	// Rendering the full component
	return (
		<div className="enrollment-info">
			<div style={{ marginLeft: 20 }}>
				{enrollmentForCurrentMember && (
					<div className="plan-overall-main-container">
						<Popover
							open={Boolean(anchorEl)}
							anchorEl={anchorEl}
							onClose={handleClose}
							anchorOrigin={{ vertical: "center", horizontal: "left" }}
							transformOrigin={{ vertical: "center", horizontal: "left" }}
							PaperProps={{ style: { width: "350px", padding: "1rem", marginLeft: "30px" } }}
						>
							<Paper style={{ boxShadow: "none" }}>
								<Typography variant="h6" fontWeight={700} color="#1a237e" gutterBottom>
									Benefits Overview
								</Typography>

								{/* Base Enrollment Table */}
								{baseEnrollment && (
									<div style={{ marginBottom: "1.5rem" }}>
										<Typography
											variant="subtitle2"
											fontWeight={600}
											color="#3949ab"
											style={{ fontSize: "15px", marginBottom: "0.5rem" }}
										>
											Base Enrollment
										</Typography>
										<div style={{ overflowX: "auto" }}>
											<table
												className="enrollment-table"
												style={{
													width: "100%",
													borderCollapse: "collapse",
													tableLayout: "fixed"
												}}
											>
												<thead>
													<tr style={{ backgroundColor: "#f5f5f5" }}>
														<th style={columnStyle}>Benefit Amount</th>
														<th style={columnStyle}>Enrollment Date</th>
														<th style={columnStyle}>Premium Amount</th>
													</tr>
												</thead>
												<tbody>
													<tr>
														<td style={cellStyle}>
															$ {formatTwoDecimalPlaces(displayBenefitAmount)}
														</td>
														<td style={cellStyle}>
															{dateTimeZoneConverter(displayEnrollmentDate)}
														</td>
														<td style={cellStyle}>
															$ {formatTwoDecimalPlaces(displayTotalPremium)}
														</td>
													</tr>
												</tbody>
											</table>
										</div>
									</div>
								)}

								{/* Stacked Enrollments Table */}
								{hasStacked && (
									<div>
										<Typography
											variant="subtitle2"
											fontWeight={600}
											color="#5e35b1"
											style={{ fontSize: "15px", marginBottom: "0.5rem" }}
										>
											Stacked Enrollments
										</Typography>
										<div style={{ overflowX: "auto" }}>
											<table
												className="enrollment-table"
												style={{
													width: "100%",
													borderCollapse: "collapse",
													tableLayout: "fixed"
												}}
											>
												<thead>
													<tr style={{ backgroundColor: "#f5f5f5" }}>
														<th style={columnStyle}>Benefit Amount</th>
														<th style={columnStyle}>Enrollment Date</th>
														<th style={columnStyle}>Premium Amount</th>
													</tr>
												</thead>
												<tbody>
													{stackedEnrollments.map((enrollment: any, index: number) => {
														if (index === 0) return null;
														return (
															<tr key={`stacked-${index}`}>
																<td style={cellStyle}>
																	${" "}
																	{formatTwoDecimalPlaces(enrollment.benefit_amount)}
																</td>
																<td style={cellStyle}>
																	{dateTimeZoneConverter(enrollment.enrollment_date)}
																</td>
																<td style={cellStyle}>
																	$ {formatTwoDecimalPlaces(enrollment.total_premium)}
																</td>
															</tr>
														);
													})}
												</tbody>
											</table>
										</div>
									</div>
								)}
							</Paper>
						</Popover>
					</div>
				)}
			</div>

			<div className="enrollment-grid">
				{/* FIELD COLUMN */}
				<div className="field-column">
					{isVebPlan && (
						<div className="field">
							<span className="title">Coverage Level:</span>
						</div>
					)}
					{isPanhandlePlan && (
						<div className="field">
							<span className="title">Sub Plan:</span>
						</div>
					)}
					<div className="field">
						<span className="title">Enrollment Date:</span>
					</div>
					<div className="field">
						<span className="title">Initial Effective Date:</span>
					</div>
					<div className="field">
						<span className="title">Effective Date:</span>
					</div>
					{showBenefitAmount && (
						<div className="field">
							<span className="title">Benefit Amount:</span>
						</div>
					)}
					<div className="field">
						<span className="title">Benefit End Date:</span>
					</div>
					<div className="field">
						<span className="title">Covered Dependents:</span>
					</div>
					{(isVebPlan || isCorePlan) && (
						<div className="field">
							<span className="title">Rider:</span>
						</div>
					)}
					{isPanhandlePlan && (
						<div className="field">
							<span className="title">Telehealth:</span>
						</div>
					)}
					{beneficiaries_required && (
						<div className="field">
							<span className="title">Beneficiary:</span>
						</div>
					)}
				</div>

				{/* ── VALUE COLUMN ── */}
				<div className="value-column">
					{isVebPlan && (
						<div className="value">
							<span>{sub_plan || "N/A"}</span>
						</div>
					)}
					{isPanhandlePlan && enrollmentsLength > 0 && (
						<div className="value">
							<span>{sub_plan || enrollments[0].sub_plan || "N/A"}</span>
						</div>
					)}
					<div className="value">
						<span>{dateTimeZoneConverter(enrollment_date)}</span>
					</div>
					<div className="value">
						<span>
							{initial_effective_date
								? dateTimeZoneConverter(initial_effective_date)
								: dateTimeZoneConverter(effective_date)}
						</span>
					</div>
					<div className="value">
						<span>{dateTimeZoneConverter(effective_date)}</span>
					</div>
					{showBenefitAmount && (
						<div className="value">
							<span>
								{benefit_amount}
								<IconButton onClick={handleBenefitViewClick}>
									<FaInfoCircle size={15} />
								</IconButton>
							</span>
						</div>
					)}
					<div className="value">
						<span>{termination_date ? dateTimeZoneConverter(termination_date) : "Current"}</span>
					</div>
					<div className="value">
						{isCorePlan ? (
							<span style={{ fontSize: 15 }}>
								{enrollmentsLength >= 1 && coreTrueCount >= 1 ? coreDependentsCount : "N/A"}
								{enrollmentsLength >= 1 && coreTrueCount >= 1 && (
									<IconButton onClick={handleClick}>
										<FaInfoCircle size={15} />
									</IconButton>
								)}
							</span>
						) : (
							<span style={{ fontSize: 15 }}>
								{enrollmentsLength >= 1 && vebTrueCount >= 1 ? vebDependentsCount : "N/A"}
								{enrollmentsLength >= 1 && vebTrueCount >= 1 && (
									<IconButton onClick={handleClick}>
										<FaInfoCircle size={15} />
									</IconButton>
								)}
							</span>
						)}
					</div>
					{(isVebPlan || isCorePlan) && (
						<div className="value">
							<span style={{ fontSize: 15 }}>
								{!vebRiderCount ? (
									"N/A"
								) : (
									<>
										{vebRiderCount}
										<IconButton onClick={handleVebRiderMenuClick}>
											<FaInfoCircle size={15} />
										</IconButton>
									</>
								)}
							</span>
						</div>
					)}
					{isPanhandlePlan && enrollmentsLength > 0 && (
						<div className="value">
							<span>{enrollments[0].is_telehealth === true ? "Teladoc Health Plan" : "N/A"}</span>
						</div>
					)}
					{beneficiaries_required && (
						<div className="value">
							<span style={{ fontSize: 15 }}>
								{filteredBenificaryLength === 0 ? (
									"N/A"
								) : (
									<IconButton onClick={handleOpenCloseEditBeneficiaryModal}>
										<FaInfoCircle size={18} />
									</IconButton>
								)}
							</span>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default EnrollmentInfoGrid;
